<script setup>
import { reactive, ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import FloatingInput from "@/shared/ui/custom/input/FloatingInput.vue";
import SectionBlock from "@/shared/ui/section-block/SectionBlock.vue";
import Button from "@/shared/ui/button/Button.vue";
import { Spinner } from "@/shared/ui/spinner";
import { normalizeRuntimeResponse } from "@/shared/serviceResponse";

const { t, locale } = useI18n();

const props = defineProps({
  selectedParticipant: { type: Object, default: null },
  saving: { type: Boolean, default: false },
});

const emit = defineEmits(["insert:field", "insert:fields", "save:participant"]);

const SERVICE_CODE = "manual";

const FIO_FIELDS = [
  "last_name",
  "first_name",
  "middle_name",
  "iin",
  "birth_date",
];

const REG_FIELDS = [
  "reg_address_country",
  "reg_address_region",
  "reg_address_district",
  "reg_address_city",
  "reg_address_street",
  "reg_address_building",
  "reg_address_corpus",
  "reg_address_flat",
];
const BIRTH_FIELDS = [
  "birth_address_country",
  "birth_address_region",
  "birth_address_district",
  "birth_address_city",
];

const labels = {
  iin: "ИИН",
  first_name: "Имя",
  last_name: "Фамилия",
  middle_name: "Отчество",
  birth_date: "Дата рождения",
  reg_address_country: "Страна",
  reg_address_region: "Область",
  reg_address_district: "Район",
  reg_address_city: "Город",
  reg_address_street: "Улица",
  reg_address_building: "Дом",
  reg_address_corpus: "Корпус",
  reg_address_flat: "Квартира",
  birth_address_country: "Страна",
  birth_address_region: "Область",
  birth_address_district: "Район",
  birth_address_city: "Город",
};

const form = reactive(
  Object.fromEntries(
    [...FIO_FIELDS, ...REG_FIELDS, ...BIRTH_FIELDS].map((key) => [key, ""])
  )
);

const participantTypeCode = computed(
  () => props.selectedParticipant?.participant_type_code || null
);

const savedSnapshot = ref(null);

function buildSnapshot() {
  return JSON.stringify(
    [...FIO_FIELDS, ...REG_FIELDS, ...BIRTH_FIELDS].reduce((acc, key) => {
      acc[key] = form[key] || "";
      return acc;
    }, {})
  );
}

const isDirty = computed(() => {
  if (savedSnapshot.value === null) return false;
  return buildSnapshot() !== savedSnapshot.value;
});

function resetForm() {
  Object.keys(form).forEach((k) => {
    form[k] = "";
  });
}

watch(
  () => props.selectedParticipant?.uuid,
  () => {
    const p = props.selectedParticipant;
    if (!p || p.participant_type !== "INDIVIDUAL") {
      resetForm();
      savedSnapshot.value = null;
      return;
    }
    form.last_name = p.last_name || "";
    form.first_name = p.first_name || "";
    form.middle_name = p.middle_name || "";
    form.iin = p.identifier || "";
    form.birth_date = p.params?.birth_date || "";

    const reg = p.params?.reg_address || {};
    form.reg_address_country = reg.country || "";
    form.reg_address_region = reg.region || "";
    form.reg_address_district = reg.district || "";
    form.reg_address_city = reg.city || "";
    form.reg_address_street = reg.street || "";
    form.reg_address_building = reg.building || "";
    form.reg_address_corpus = reg.corpus || "";
    form.reg_address_flat = reg.flat || "";

    const birth = p.params?.birth_address || {};
    form.birth_address_country = birth.country || "";
    form.birth_address_region = birth.region || "";
    form.birth_address_district = birth.district || "";
    form.birth_address_city = birth.city || "";

    savedSnapshot.value = buildSnapshot();
  },
  { immediate: true }
);

function buildManualBlob() {
  const dict = (val) =>
    val ? { name: val, name_ru: val, name_kk: val } : null;

  return {
    iin: form.iin || null,
    first_name: form.first_name || null,
    last_name: form.last_name || null,
    middle_name: form.middle_name || null,
    birth_date: form.birth_date || null,
    reg_address: {
      country: dict(form.reg_address_country),
      region: dict(form.reg_address_region),
      district: dict(form.reg_address_district),
      city: form.reg_address_city || null,
      street: form.reg_address_street || null,
      building: form.reg_address_building || null,
      corpus: form.reg_address_corpus || null,
      flat: form.reg_address_flat || null,
    },
    birth_address: {
      country: dict(form.birth_address_country),
      region: dict(form.birth_address_region),
      district: dict(form.birth_address_district),
      city: form.birth_address_city || null,
    },
  };
}

function onSave() {
  const p = props.selectedParticipant;
  if (!p?.uuid || props.saving) return;

  const existingServiceFields = p.service_fields || {};

  const payload = {
    participant_type: p.participant_type,
    participant_type_code: p.participant_type_code,
    identifier: form.iin || "",
    first_name: form.first_name || "",
    last_name: form.last_name || "",
    middle_name: form.middle_name || "",
    params: {
      ...(p.params || {}),
      birth_date: form.birth_date || "",
      reg_address: {
        country: form.reg_address_country || "",
        region: form.reg_address_region || "",
        district: form.reg_address_district || "",
        city: form.reg_address_city || "",
        street: form.reg_address_street || "",
        building: form.reg_address_building || "",
        corpus: form.reg_address_corpus || "",
        flat: form.reg_address_flat || "",
      },
      birth_address: {
        country: form.birth_address_country || "",
        region: form.birth_address_region || "",
        district: form.birth_address_district || "",
        city: form.birth_address_city || "",
      },
    },
    service_fields: {
      ...existingServiceFields,
      [SERVICE_CODE]: buildManualBlob(),
    },
  };

  emit("save:participant", { uuid: p.uuid, payload });
}

function buildField(key, value) {
  return {
    key,
    value,
    participant_type_code: participantTypeCode.value,
    service_code: SERVICE_CODE,
  };
}

function onSingleInsert(payload) {
  if (!payload?.value) return;
  emit("insert:field", buildField(payload.key, payload.value));
}

function applyAll() {
  const fields = normalizeRuntimeResponse(buildManualBlob(), {
    locale: locale.value,
    participantTypeCode: participantTypeCode.value,
    serviceCode: SERVICE_CODE,
  });

  if (fields.length) emit("insert:fields", fields);
}
</script>

<template>
  <div class="gbdfl-block">
    <div class="flex justify-between items-center">
      <div class="flex items-center gap-3 flex-1 h-10">
        <h2 class="text-gray-900 text-lg font-semibold leading-6">
          {{ $t("Сведения о лице") }}
        </h2>
      </div>
    </div>

    <SectionBlock>
      <div class="flex flex-col gap-2.5">
        <FloatingInput
          v-for="key in FIO_FIELDS"
          :key="key"
          v-model="form[key]"
          :key-field="key"
          :label="t(labels[key])"
          class="w-full"
          @insert:field="onSingleInsert"
        />
      </div>
    </SectionBlock>

    <SectionBlock :title="$t('Адрес ')">
      <div class="flex flex-col gap-2.5">
        <FloatingInput
          v-for="key in REG_FIELDS"
          :key="key"
          v-model="form[key]"
          :key-field="key"
          :label="t(labels[key])"
          class="w-full"
          @insert:field="onSingleInsert"
        />
      </div>
    </SectionBlock>

    <SectionBlock :title="$t('Адрес рождения')">
      <div class="flex flex-col gap-2.5">
        <FloatingInput
          v-for="key in BIRTH_FIELDS"
          :key="key"
          v-model="form[key]"
          :key-field="key"
          :label="t(labels[key])"
          class="w-full"
          @insert:field="onSingleInsert"
        />
      </div>
    </SectionBlock>

    <div class="footer flex flex-col gap-2">
      <Button variant="outline" class="w-full" @click="applyAll">
        {{ $t("Добавить в документ") }}
      </Button>
      <Button
        v-if="isDirty"
        class="w-full"
        variant="outline"
        :disabled="saving"
        @click="onSave"
      >
        <Spinner v-if="saving" size="sm" class="mr-2" />
        {{ $t("Сохранить данные") }}
      </Button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.gbdfl-block {
  @apply flex flex-col h-full w-full overflow-y-auto gap-6;
}
</style>
